import { ref, computed } from "vue";
import type { TutorialStep } from "@components/shared/lila/OnboardingTutorial/tutorialSteps";
import { tutorialSteps } from "@components/shared/lila/OnboardingTutorial/tutorialSteps";

export interface TutorialState {
  isActive: boolean;
  currentIndex: number;
  steps: TutorialStep[];
}

const state = ref<TutorialState>({
  isActive: false,
  currentIndex: 0,
  steps: tutorialSteps,
});

export default function useTutorial() {
  const isActive = computed(() => state.value.isActive);
  const steps = computed(() => state.value.steps);
  const currentIndex = computed(() => state.value.currentIndex);
  const currentStepData = computed<TutorialStep | null>(
    () => state.value.steps[state.value.currentIndex] ?? null
  );
  const totalSteps = computed(() => state.value.steps.length);
  const displayStep = computed(() => state.value.currentIndex + 1);
  const isLastStep = computed(
    () => state.value.currentIndex >= state.value.steps.length - 1
  );

  function startTutorial(stepList: TutorialStep[]) {
    if (state.value.isActive) return;

    state.value.steps = stepList.length > 0 ? stepList : tutorialSteps;
    state.value.currentIndex = 0;
    state.value.isActive = true;
  }

  function nextStep() {
    if (isLastStep.value) {
      completeTutorial();
      return;
    }

    state.value.currentIndex++;
  }

  function goToIndex(index: number) {
    if (index >= 0 && index < state.value.steps.length) {
      state.value.currentIndex = index;
    }
  }

  function skipTutorial() {
    completeTutorial();
  }

  function completeTutorial() {
    state.value.isActive = false;
    state.value.currentIndex = 0;
  }

  return {
    isActive,
    steps,
    currentIndex,
    currentStepData,
    totalSteps,
    displayStep,
    isLastStep,
    startTutorial,
    nextStep,
    goToIndex,
    skipTutorial,
    completeTutorial,
  };
}
